import { useState, useEffect, useRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { Loader2, Send, XCircle, Clock, MessageSquareText, ShieldCheck } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import { stringifyDetail } from '../lib/utils';
import { parseUtc } from '../lib/datetime';

const API_BASE = window.location.origin;

const formatTimestamp = (iso) => {
    try {
        const d = parseUtc(iso);
        const now = new Date();
        const diffMs = now - d;
        const diffH = diffMs / (1000 * 60 * 60);

        if (diffH < 1) {
            const mins = Math.floor(diffMs / 60000);
            return mins < 2 ? 'Just now' : `${mins} minutes ago`;
        }
        if (diffH < 24) return `${Math.floor(diffH)} hours ago`;

        return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
    } catch {
        return '';
    }
};

const formatExpiry = (iso) => {
    try {
        const exp = parseUtc(iso);
        const now = new Date();
        const hoursLeft = Math.max(0, (exp - now) / (1000 * 60 * 60));
        if (hoursLeft < 1) return 'Less than 1 hour remaining';
        if (hoursLeft < 24) return `${Math.floor(hoursLeft)} hours remaining`;
        return `${Math.floor(hoursLeft / 24)} days remaining`;
    } catch {
        return '';
    }
};

export default function RecoveryBridge() {
    const { t } = useTranslation();
    const { token } = useParams();
    const [session, setSession] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [replyText, setReplyText] = useState('');
    const [sending, setSending] = useState(false);
    const [stopping, setStopping] = useState(false);
    const [stopped, setStopped] = useState(false);
    const [sendError, setSendError] = useState(null);
    const messagesEndRef = useRef(null);
    const pollRef = useRef(null);

    const fetchSession = useCallback(async () => {
        try {
            const res = await fetch(`${API_BASE}/api/recovery/bridge/${token}`);
            if (res.status === 404 || res.status === 410) {
                setError('expired');
                return;
            }
            if (!res.ok) {
                setError('failed');
                return;
            }
            const data = await res.json();
            setSession(data);
            if (data.customer_stopped || data.status === 'stopped') {
                setStopped(true);
            }
        } catch {
            setError('failed');
        } finally {
            setLoading(false);
        }
    }, [token]);

    useEffect(() => {
        fetchSession();
        // Poll every 30 seconds — async, no urgency
        pollRef.current = setInterval(fetchSession, 30000);
        return () => clearInterval(pollRef.current);
    }, [fetchSession]);

    useEffect(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, [session?.messages]);

    const handleReply = async () => {
        if (!replyText.trim() || sending) return;
        setSending(true);
        setSendError(null);
        try {
            const res = await fetch(`${API_BASE}/api/recovery/bridge/${token}/reply`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ text: replyText.trim() })
            });
            if (res.status === 429) {
                setSendError('Please wait a moment before sending another message.');
                return;
            }
            if (!res.ok) {
                const data = await res.json().catch(() => ({}));
                setSendError(stringifyDetail(data.detail) || 'Unable to send. Please try again.');
                return;
            }
            setReplyText('');
            await fetchSession();
        } catch {
            setSendError('Unable to send. Please check your connection.');
        } finally {
            setSending(false);
        }
    };

    const handleStop = async () => {
        if (stopping) return;
        setStopping(true);
        try {
            await fetch(`${API_BASE}/api/recovery/bridge/${token}/stop`, { method: 'POST' });
            setStopped(true);
            clearInterval(pollRef.current);
        } catch {
            // Silently handle
        } finally {
            setStopping(false);
        }
    };

    // --- Loading ---
    if (loading) {
        return (
            <div className="min-h-screen bg-gradient-to-b from-slate-900 to-slate-800 flex items-center justify-center" data-testid="bridge-loading">
                <div className="text-center">
                    <Loader2 className="w-10 h-10 animate-spin text-slate-400 mx-auto" />
                    <p className="text-slate-400 mt-4 text-sm">Loading...</p>
                </div>
            </div>
        );
    }

    // --- Expired / Not Found ---
    if (error === 'expired') {
        return (
            <div className="min-h-screen bg-gradient-to-b from-slate-900 to-slate-800 flex items-center justify-center px-5" data-testid="bridge-expired">
                <div className="text-center max-w-sm">
                    <div className="w-14 h-14 bg-slate-700/50 rounded-full flex items-center justify-center mx-auto mb-4">
                        <Clock className="w-7 h-7 text-slate-400" />
                    </div>
                    <h1 className="text-lg font-semibold text-white mb-2" style={{ fontFamily: 'Manrope, sans-serif' }}>{t('recoveryBridge.conversationEnded')}</h1>
                    <p className="text-slate-400 text-sm leading-relaxed">The link you followed is no longer active. If you need further assistance, please contact the restaurant directly.</p>
                </div>
            </div>
        );
    }

    // --- Error ---
    if (error === 'failed' || !session) {
        return (
            <div className="min-h-screen bg-gradient-to-b from-slate-900 to-slate-800 flex items-center justify-center px-5" data-testid="bridge-error">
                <div className="text-center max-w-sm">
                    <h1 className="text-lg font-semibold text-white mb-2" style={{ fontFamily: 'Manrope, sans-serif' }}>{t('recoveryBridge.unableToLoad')}</h1>
                    <p className="text-slate-400 text-sm">Please try again later or contact the restaurant directly.</p>
                </div>
            </div>
        );
    }

    // --- Stopped / Ended ---
    if (stopped) {
        return (
            <div className="min-h-screen bg-gradient-to-b from-slate-900 to-slate-800 flex items-center justify-center px-5" data-testid="bridge-stopped">
                <div className="text-center max-w-sm">
                    <div className="w-14 h-14 bg-emerald-500/10 rounded-full flex items-center justify-center mx-auto mb-4">
                        <ShieldCheck className="w-7 h-7 text-emerald-400" />
                    </div>
                    <h1 className="text-lg font-semibold text-white mb-2" style={{ fontFamily: 'Manrope, sans-serif' }}>{t('recoveryBridge.conversationEndedShort')}</h1>
                    <p className="text-slate-400 text-sm leading-relaxed">Thank you for your time. This conversation is now closed.</p>
                </div>
            </div>
        );
    }

    const messages = session.messages || [];
    const hasMessages = messages.length > 0;

    return (
        <div className="min-h-screen bg-gradient-to-b from-slate-900 to-slate-800 flex flex-col" data-testid="bridge-page">
            {/* Header */}
            <header className="sticky top-0 z-10 bg-slate-900/90 backdrop-blur-sm border-b border-white/5 px-5 py-4" data-testid="bridge-header">
                <div className="max-w-lg mx-auto">
                    <p className="text-slate-500 text-xs uppercase tracking-wider mb-0.5">{t('recoveryBridge.messageFrom')}</p>
                    <h1 className="text-white font-semibold text-base" style={{ fontFamily: 'Manrope, sans-serif' }} data-testid="bridge-org-name">
                        {session.organization_name}
                    </h1>
                    {session.location_name && (
                        <p className="text-slate-400 text-xs" data-testid="bridge-location-name">{session.location_name}</p>
                    )}
                </div>
            </header>

            {/* Messages Area */}
            <main className="flex-1 overflow-y-auto px-5 py-6">
                <div className="max-w-lg mx-auto space-y-5">
                    {/* Expiry notice — subtle, not urgent */}
                    {session.expires_at && (
                        <div className="flex items-center gap-2 text-slate-500 text-xs px-1" data-testid="bridge-expiry">
                            <Clock className="w-3 h-3 shrink-0" />
                            <span>This conversation will close automatically. {formatExpiry(session.expires_at)}</span>
                        </div>
                    )}

                    {!hasMessages && (
                        <div className="text-center py-8" data-testid="bridge-no-messages">
                            <MessageSquareText className="w-8 h-8 text-slate-600 mx-auto mb-3" />
                            <p className="text-slate-500 text-sm">No messages yet.</p>
                            <p className="text-slate-600 text-xs mt-1">You will see messages here when the management team writes to you.</p>
                        </div>
                    )}

                    {messages.map((msg) => {
                        const isManager = msg.sender === 'manager';
                        return (
                            <div
                                key={msg.id}
                                className={`${isManager ? 'pr-8' : 'pl-8'}`}
                                data-testid={`bridge-message-${msg.id}`}
                            >
                                <div className={`rounded-2xl px-4 py-3 ${
                                    isManager
                                        ? 'bg-white/5 border border-white/8'
                                        : 'bg-emerald-900/20 border border-emerald-500/10'
                                }`}>
                                    <p className="text-xs font-medium mb-1.5" style={{
                                        color: isManager ? 'rgb(148, 163, 184)' : 'rgb(110, 231, 183)'
                                    }}>
                                        {isManager ? (msg.sender_name || 'Management') : 'You'}
                                    </p>
                                    <p className="text-white text-sm leading-relaxed whitespace-pre-wrap">{msg.text}</p>
                                    <p className="text-slate-600 text-xs mt-2">{formatTimestamp(msg.timestamp)}</p>
                                </div>
                            </div>
                        );
                    })}
                    <div ref={messagesEndRef} />
                </div>
            </main>

            {/* Reply Area */}
            <footer className="sticky bottom-0 bg-slate-900/95 backdrop-blur-sm border-t border-white/5 px-5 py-4" data-testid="bridge-footer">
                <div className="max-w-lg mx-auto space-y-3">
                    {sendError && (
                        <p className="text-red-400 text-xs" data-testid="bridge-send-error">{sendError}</p>
                    )}

                    <div className="flex gap-2">
                        <Textarea
                            value={replyText}
                            onChange={(e) => setReplyText(e.target.value)}
                            placeholder="Leave a reply..."
                            maxLength={2000}
                            rows={2}
                            className="flex-1 bg-white/5 border-white/10 text-white placeholder:text-slate-500 rounded-xl resize-none text-sm focus:border-slate-400/30"
                            onKeyDown={(e) => {
                                if (e.key === 'Enter' && !e.shiftKey) {
                                    e.preventDefault();
                                    handleReply();
                                }
                            }}
                            data-testid="bridge-reply-input"
                        />
                        <Button
                            onClick={handleReply}
                            disabled={!replyText.trim() || sending}
                            size="icon"
                            className="h-auto bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl px-3 disabled:opacity-30 self-end"
                            data-testid="bridge-reply-button"
                        >
                            {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                        </Button>
                    </div>

                    {/* End Conversation — always visible */}
                    <button
                        onClick={handleStop}
                        disabled={stopping}
                        className="w-full flex items-center justify-center gap-2 py-2 text-slate-500 hover:text-red-400 text-xs transition-colors"
                        data-testid="bridge-end-conversation"
                    >
                        <XCircle className="w-3.5 h-3.5" />
                        {stopping ? 'Ending...' : 'End Conversation'}
                    </button>
                </div>
            </footer>
        </div>
    );
}
